import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkle, CheckSquare, Warning } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITES = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'normal', label: 'Normal' },
  { value: 'basse', label: 'Basse' },
];

export default function CreateTasksFromCRCDialog({ open, onClose, chantierId, crc, onCreated }) {
  const [step, setStep] = useState(0);
  const [propositions, setPropositions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open && crc?.id) {
      loadPropositions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, crc?.id]);

  const loadPropositions = async () => {
    setStep(0);
    setPropositions([]);
    setSelected(new Set());
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/generer-taches`,
        {},
        { withCredentials: true, timeout: 90_000 }
      );
      const props = data.propositions || [];
      setPropositions(props);
      setSelected(new Set(props.map((p) => p.point_id)));
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Génération échouée');
      onClose();
    }
  };

  const toggleSelection = (pointId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pointId)) next.delete(pointId);
      else next.add(pointId);
      return next;
    });
  };

  const updateProposition = (pointId, field, value) => {
    setPropositions((prev) => prev.map((p) => (p.point_id === pointId ? { ...p, [field]: value } : p)));
  };

  const handleCreate = async () => {
    const taches = propositions.filter((p) => selected.has(p.point_id));
    if (taches.length === 0) {
      toast.error('Sélectionnez au moins une action');
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/crc/${crc.id}/creer-taches-batch`,
        { taches },
        { withCredentials: true }
      );
      onCreated(data.created_count);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Création échouée');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="crc-tasks-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }} className="text-2xl">
            Créer les actions associées au compte rendu
          </DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-3 py-8">
            <div className="flex items-center gap-2 text-[#C4773B]">
              <Sparkle size={20} weight="fill" className="animate-pulse" />
              <span className="text-sm font-medium">L&apos;Assistant analyse les points du compte rendu...</span>
            </div>
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        )}

        {step === 1 && propositions.length === 0 && (
          <div className="text-center py-8 text-[#6B6B6B]" data-testid="crc-tasks-no-points">
            <Warning size={32} className="mx-auto mb-2 text-[#9A9A9A]" />
            <p className="text-sm">Aucun point ouvert dans ce compte rendu. Pas d&apos;action à créer.</p>
          </div>
        )}

        {step === 1 && propositions.length > 0 && (
          <div className="space-y-3">
            <div className="bg-[#FDF6EC] border-l-4 border-[#C4773B] p-3 text-xs text-[#6B4A24]">
              <p className="font-semibold uppercase tracking-wide mb-1">
                L&apos;Assistant a proposé {propositions.length} action{propositions.length > 1 ? 's' : ''}
              </p>
              <p>Coche celles à créer. Tu peux modifier titre, description, priorité, échéance et entreprise avant validation.</p>
            </div>

            {propositions.map((p) => (
              <div
                key={p.point_id}
                className={`border ${selected.has(p.point_id) ? 'border-[#C4773B] bg-[#FDF6EC]/40' : 'border-[#E0E0DB]'} p-3`}
                data-testid={`crc-task-row-${p.point_id}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(p.point_id)}
                    onChange={() => toggleSelection(p.point_id)}
                    className="mt-1"
                    data-testid={`crc-task-checkbox-${p.point_id}`}
                  />
                  <div className="flex-1 space-y-2">
                    <div className="text-[10px] text-[#9A9A9A] uppercase tracking-wider">
                      Point n°{p.point_numero ?? '?'} — {p.point_description?.substring(0, 80)}
                      {p.point_description?.length > 80 ? '…' : ''}
                    </div>
                    <Input
                      value={p.titre}
                      onChange={(e) => updateProposition(p.point_id, 'titre', e.target.value)}
                      className="rounded-none font-medium"
                      placeholder="Titre de l'action"
                    />
                    <Textarea
                      value={p.description}
                      onChange={(e) => updateProposition(p.point_id, 'description', e.target.value)}
                      rows={2}
                      className="rounded-none text-xs"
                      placeholder="Description détaillée"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Priorité</Label>
                        <Select value={p.priorite} onValueChange={(v) => updateProposition(p.point_id, 'priorite', v)}>
                          <SelectTrigger className="rounded-none h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRIORITES.map((pr) => (
                              <SelectItem key={pr.value} value={pr.value}>
                                {pr.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Échéance</Label>
                        <Input
                          type="date"
                          value={(p.echeance || '').substring(0, 10)}
                          onChange={(e) => updateProposition(p.point_id, 'echeance', e.target.value)}
                          className="rounded-none h-8 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Entreprise</Label>
                        <Input
                          value={p.entreprise || ''}
                          onChange={(e) => updateProposition(p.point_id, 'entreprise', e.target.value)}
                          className="rounded-none h-8 text-xs"
                          placeholder="Nom entreprise"
                        />
                      </div>
                    </div>
                    {p.lot && (
                      <div className="text-[10px] text-[#6B6B6B]">
                        Lot : {p.lot}
                        {p.piece_nom ? ` · ${p.piece_nom}` : ''}
                      </div>
                    )}
                    {p.raison_priorite && <div className="text-[10px] italic text-[#9A9A9A]">{p.raison_priorite}</div>}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-between items-center pt-4 border-t border-[#E0E0DB]">
              <span className="text-xs text-[#6B6B6B]">
                {selected.size}/{propositions.length} sélectionnée{selected.size > 1 ? 's' : ''}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} className="rounded-none">
                  Annuler
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || selected.size === 0}
                  className="bg-[#C4773B] hover:bg-[#A5623A] text-white rounded-none gap-2"
                  data-testid="crc-tasks-create-batch"
                >
                  <CheckSquare size={14} weight="bold" />
                  {creating ? 'Création...' : `Créer ${selected.size} action${selected.size > 1 ? 's' : ''}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
